import { Link } from 'react-router-dom'
import { CLASES_DE_NIVEL } from './etiquetas'
import type { Fila, Matriz, NivelDeRiesgo, Tarea } from './tipos'

const NIVELES: { id: NivelDeRiesgo; etiqueta: string }[] = [
  { id: 'intolerable', etiqueta: 'Intolerable' },
  { id: 'importante', etiqueta: 'Importante' },
  { id: 'moderado', etiqueta: 'Moderado' },
  { id: 'tolerable', etiqueta: 'Tolerable' },
]

function estadoDeTarea(tarea: Tarea, filas: Fila[]) {
  if (tarea.estado === 'propuesta') return { texto: 'Validar tarea', clase: 'bg-atencion-fondo text-atencion-texto' }
  if (filas.length === 0) return { texto: 'Sin peligros registrados', clase: 'bg-atencion-fondo text-atencion-texto' }
  if (filas.some((fila) => fila.estado === 'propuesta')) return { texto: 'Revisar peligros', clase: 'bg-atencion-fondo text-atencion-texto' }
  return { texto: 'Filas revisadas', clase: 'bg-conforme-fondo text-conforme-texto' }
}

/** Lectura del estado persistido de la matriz; no propone ni modifica evaluaciones. */
export function PanelDeMatriz({ matriz }: { matriz: Matriz }) {
  const tareas = matriz.tareas.filter((tarea) => tarea.estado !== 'descartada')
  const filas = matriz.filas.filter((fila) => fila.estado !== 'descartada')
  const tareasValidadas = tareas.filter((tarea) => tarea.estado === 'validada')
  const cubiertas = tareasValidadas.filter((tarea) => filas.some((fila) => fila.tarea_id === tarea.id)).length
  const evaluacionesAnteriores = filas.filter((fila) => fila.version_motor !== 'evaluacion-isp-v3').length
  const protocolosPendientes = filas.filter((fila) => fila.evaluacion.tipo === 'protocolo' && !fila.evaluacion.consolidada).length
  const porNivel = NIVELES.map((nivel) => ({
    ...nivel,
    cantidad: filas.filter((fila) => fila.version_motor === 'evaluacion-isp-v3' && fila.evaluacion.tipo === 'motor' && fila.evaluacion.nivel === nivel.id).length,
  }))
  const ruta = `/matrices/${matriz.id}`
  const siguientePaso = matriz.estado === 'oficial' || matriz.bloqueos_de_confirmacion.length === 0
    ? 'oficializar'
    : matriz.progreso.tareas_propuestas > 0 || tareas.length === 0
      ? 'levantamiento'
      : 'matriz'

  return (
    <div className="grid gap-5" aria-labelledby="titulo-panel-matriz">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="cs-eyebrow">Vista general · MIPER</p>
          <h2 id="titulo-panel-matriz" className="text-h2">Avance de esta matriz</h2>
          <p className="text-pequeno text-texto-secundario">Datos de la revisión actual. La evaluación y el visto bueno permanecen en cada fila.</p>
        </div>
        <Link to={`${ruta}/${siguientePaso}`} className="sst-cta">
          {matriz.estado === 'oficial' ? 'Ver revisión oficial' : 'Continuar revisión'}
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" role="group" aria-label="Indicadores de avance">
        <Indicador etiqueta="Tareas validadas" valor={`${tareasValidadas.length} / ${tareas.length}`} detalle="Las tareas propuestas requieren visto bueno" />
        <Indicador etiqueta="Tareas con peligros" valor={`${cubiertas} / ${tareasValidadas.length}`} detalle="Cobertura de tareas validadas" />
        <Indicador etiqueta="Filas revisadas" valor={`${matriz.progreso.filas_validadas} / ${filas.length}`} detalle={`${matriz.progreso.filas_propuestas} pendientes de revisión`} />
        <Indicador etiqueta="Estado" valor={matriz.estado === 'oficial' ? `Oficial · rev. ${matriz.revision_actual}` : 'Borrador'} detalle={matriz.estado === 'oficial' ? 'Revisión confirmada' : `${matriz.bloqueos_de_confirmacion.length} bloqueos para oficializar`} />
      </div>

      <section className="sst-tarjeta grid gap-3 p-4 sm:p-5" aria-labelledby="titulo-cobertura">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="titulo-cobertura" className="text-h3">Cobertura por tarea</h3>
          <Link to={`${ruta}/levantamiento`} className="text-pequeno font-semibold text-accion underline">Revisar levantamiento</Link>
        </div>
        {tareas.length === 0 ? <p className="text-pequeno text-texto-secundario">Aún no hay tareas. Completa el levantamiento para comenzar.</p> : (
          <ul className="grid gap-2">
            {tareas.map((tarea) => {
              const deTarea = filas.filter((fila) => fila.tarea_id === tarea.id)
              const estado = estadoDeTarea(tarea, deTarea)
              return (
                <li key={tarea.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-borde p-3">
                  <div className="min-w-0">
                    <p className="text-cuerpo font-semibold">{tarea.tarea}</p>
                    <p className="text-pequeno text-texto-secundario">{tarea.proceso} · {tarea.area} · {tarea.puesto} · {deTarea.length} peligro(s)</p>
                  </div>
                  <span className={`sst-nivel ${estado.clase}`}>{estado.texto}</span>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <div className="grid gap-3 lg:grid-cols-2">
        <section className="sst-tarjeta grid content-start gap-3 p-4 sm:p-5" aria-labelledby="titulo-niveles">
          <h3 id="titulo-niveles" className="text-h3">Riesgos de seguridad y emergencias</h3>
          <p className="text-pequeno text-texto-secundario">Filas activas evaluadas con el motor ISP v3. Otros riesgos requieren su protocolo.</p>
          <ul className="grid gap-2">
            {porNivel.map((nivel) => (
              <li key={nivel.id} className="flex items-center justify-between gap-2">
                <span className={`sst-nivel ${CLASES_DE_NIVEL[nivel.id]}`}>{nivel.etiqueta}</span>
                <strong className="text-cuerpo">{nivel.cantidad}</strong>
              </li>
            ))}
          </ul>
        </section>
        <section className="sst-tarjeta grid content-start gap-3 p-4 sm:p-5" aria-labelledby="titulo-pendientes">
          <h3 id="titulo-pendientes" className="text-h3">Trabajo pendiente</h3>
          <ul className="grid gap-2 text-pequeno">
            <li>{matriz.progreso.tareas_propuestas} tarea(s) por validar</li>
            <li>{matriz.progreso.tareas_sin_filas} tarea(s) validadas sin peligros registrados</li>
            <li>{matriz.progreso.filas_propuestas} fila(s) por revisar</li>
            <li>{protocolosPendientes} evaluación(es) pendientes de protocolo</li>
            <li>{evaluacionesAnteriores} evaluación(es) anteriores por reevaluar</li>
          </ul>
          <Link to={`${ruta}/matriz`} className="text-pequeno font-semibold text-accion underline">Revisar peligros y controles</Link>
        </section>
      </div>

      {matriz.bloqueos_de_confirmacion.length > 0 ? (
        <section className="rounded-lg border border-atencion/60 bg-atencion-fondo p-4" aria-labelledby="titulo-bloqueos">
          <h3 id="titulo-bloqueos" className="text-h3 text-atencion-texto">Antes de oficializar</h3>
          <ul className="mt-2 list-disc pl-5 text-pequeno text-atencion-texto">
            {matriz.bloqueos_de_confirmacion.map((bloqueo) => <li key={bloqueo.codigo}>{bloqueo.mensaje}</li>)}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

function Indicador({ etiqueta, valor, detalle }: { etiqueta: string; valor: string; detalle: string }) {
  return (
    <div className="sst-tarjeta sst-indicador">
      <span>{etiqueta}</span>
      <strong>{valor}</strong>
      <small>{detalle}</small>
    </div>
  )
}
